import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Menu, ArrowRight, ChevronRight } from 'lucide-react';

export default function Header() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const services = [
    { to: '/services/renovation-complete', label: 'Rénovation complète', desc: 'Transformation totale pilotée' },
    { to: '/services/salle-de-bain', label: 'Salle de bain', desc: 'Rénovation complète SDB' },
    { to: '/services/cuisine', label: 'Cuisine', desc: 'Installation pilotée' },
    { to: '/services/plans-3d', label: 'Plans 3D', desc: 'Visualisation photo-réaliste' },
    { to: '/suivi-de-chantier', label: 'Suivi de chantier', desc: 'Notre différenciateur' }
  ];

  const navLinks = [
    { to: '/galerie-avant-apres', label: 'Galerie' },
    { to: '/faq', label: 'FAQ' }
  ];

  return (
    <header 
      className="sticky top-0 z-50 border-b backdrop-blur-md"
      style={{ 
        backgroundColor: 'rgba(245,243,239,0.85)', 
        borderColor: 'var(--hr-border)'
      }}
      data-testid="main-header"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <h1 className="font-['Playfair_Display'] text-xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Homely Rénovation
            </h1>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-8">
            {/* Services Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className="text-sm font-medium"
                    style={{ color: 'var(--hr-text)' }}
                  >
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] p-4">
                      <div className="mb-3">
                        <Link
                          to="/services"
                          className="block p-3 rounded-lg hover:bg-[color:var(--hr-bg-alt)] transition-colors"
                        >
                          <div className="font-['Figtree'] font-semibold text-sm mb-1" style={{ color: 'var(--hr-text)' }}>
                            Voir tous les services
                          </div>
                          <div className="text-xs" style={{ color: 'var(--hr-text-muted)' }}>
                            Découvrez notre offre complète
                          </div>
                        </Link>
                      </div>
                      <div className="h-px" style={{ backgroundColor: 'var(--hr-border)' }} />
                      <div className="mt-3 space-y-1">
                        {services.map((service) => (
                          <Link
                            key={service.to}
                            to={service.to}
                            className="block p-3 rounded-lg hover:bg-[color:var(--hr-bg-alt)] transition-colors group"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-['Figtree'] font-medium text-sm mb-0.5" style={{ color: 'var(--hr-text)' }}>
                                  {service.label}
                                </div>
                                <div className="text-xs" style={{ color: 'var(--hr-text-muted)' }}>
                                  {service.desc}
                                </div>
                              </div>
                              <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--hr-primary)' }} />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-medium hover:opacity-70 transition-opacity"
                style={{ 
                  color: location.pathname === link.to ? 'var(--hr-primary)' : 'var(--hr-text)'
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:block">
            <Button
              size="sm"
              className="rounded-xl"
              style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
              asChild
            >
              <Link to="/devis">
                Demander un devis
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="rounded-xl">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]" style={{ backgroundColor: 'var(--hr-bg)' }}>
              <div className="flex flex-col space-y-4 mt-8">
                <div>
                  <p className="font-['Figtree'] font-semibold text-sm mb-2" style={{ color: 'var(--hr-text)' }}>Services</p>
                  <div className="space-y-1 ml-3">
                    <Link
                      to="/services"
                      onClick={() => setIsOpen(false)}
                      className="block text-sm py-2"
                      style={{ color: 'var(--hr-text-muted)' }}
                    >
                      Tous les services
                    </Link>
                    {services.map((service) => (
                      <Link
                        key={service.to}
                        to={service.to}
                        onClick={() => setIsOpen(false)}
                        className="block text-sm py-2"
                        style={{ color: 'var(--hr-text-muted)' }}
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                </div>
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsOpen(false)}
                    className="text-base font-medium py-2"
                    style={{ color: 'var(--hr-text)' }}
                  >
                    {link.label}
                  </Link>
                ))}
                <Button
                  className="rounded-xl w-full mt-4"
                  style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                  asChild
                  onClick={() => setIsOpen(false)}
                >
                  <Link to="/devis">Demander un devis</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}